import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Plus, Edit3, Trash2, X, MessageSquare, Copy } from 'lucide-react';
import { safeFetch } from '../../utils/safeFetch';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VARIABLE_LABELS = {
  customer_name: 'Customer Name',
  customer_mobile: 'Customer Mobile',
  booking_ref: 'Booking Ref',
  site_address: 'Site Address',
  collection_date: 'Collection Date',
  collection_time: 'Collection Time',
  driver_name: 'Driver Name',
  client_business_name: 'Client Business',
  service_type: 'Service Type',
  eta: 'ETA',
};

const TemplateModal = ({ template, variables, onSave, onClose }) => {
  const [name, setName] = useState(template?.name || '');
  const [body, setBody] = useState(template?.body || '');
  const [description, setDescription] = useState(template?.description || '');
  const [saving, setSaving] = useState(false);
  const bodyRef = React.useRef(null);

  const insertVariable = (v) => {
    const ta = bodyRef.current;
    if (!ta) {
      setBody(b => `${b}{{${v}}}`);
      return;
    }
    const start = ta.selectionStart ?? body.length;
    const end = ta.selectionEnd ?? body.length;
    const next = `${body.slice(0, start)}{{${v}}}${body.slice(end)}`;
    setBody(next);
    setTimeout(() => {
      ta.focus();
      const pos = start + v.length + 4;
      ta.setSelectionRange(pos, pos);
    }, 0);
  };

  const handleSave = async () => {
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    await onSave({ name: name.trim(), body: body.trim(), description: description.trim() });
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="sms-template-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#E2E8F0]">
          <h2 className="text-lg font-semibold text-[#111111]">
            {template ? 'Edit Template' : 'New SMS Template'}
          </h2>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#111111]" data-testid="close-template-modal">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 uppercase tracking-wide">Template Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. On the way"
              data-testid="template-name-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 uppercase tracking-wide">Description (optional)</label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Short description for the team"
              data-testid="template-description-input"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-1.5 uppercase tracking-wide">Message Body</label>
            <textarea
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={6}
              placeholder="Hi {{customer_name}}, your driver is on the way to {{site_address}}."
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
              data-testid="template-body-input"
            />
            <p className="text-xs text-[#64748B] mt-1">{body.length} characters {body.length > 160 ? `(${Math.ceil(body.length / 160)} SMS segments)` : '(1 SMS segment)'}</p>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#64748B] mb-2 uppercase tracking-wide">Insert Variable</label>
            <div className="flex flex-wrap gap-2">
              {variables.map(v => (
                <button
                  key={v}
                  type="button"
                  onClick={() => insertVariable(v)}
                  className="text-xs px-2.5 py-1 rounded-md bg-[#F1F5F9] hover:bg-[#0D9488] hover:text-white border border-[#E2E8F0] transition-colors"
                  data-testid={`insert-var-${v}`}
                >
                  {`{{${v}}}`}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 p-5 border-t border-[#E2E8F0] bg-[#F8FAFC]">
          <Button variant="outline" onClick={onClose} data-testid="cancel-template-btn">Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={saving || !name.trim() || !body.trim()}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
            data-testid="save-template-btn"
          >
            {saving ? 'Saving...' : 'Save Template'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const AdminSMSTemplates = () => {
  const [templates, setTemplates] = useState([]);
  const [variables, setVariables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('admin_token');
    const res = await safeFetch(`${API_URL}/api/admin/sms-templates`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setTemplates(res.data.templates || []);
      setVariables(res.data.variables || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSave = async (data) => {
    const token = localStorage.getItem('admin_token');
    const url = editing
      ? `${API_URL}/api/admin/sms-templates/${editing.id}`
      : `${API_URL}/api/admin/sms-templates`;
    const method = editing ? 'PUT' : 'POST';
    const res = await safeFetch(url, {
      method,
      headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (res.ok) {
      setShowModal(false);
      setEditing(null);
      await fetchTemplates();
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    const token = localStorage.getItem('admin_token');
    await safeFetch(`${API_URL}/api/admin/sms-templates/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    });
    await fetchTemplates();
  };

  const copyBody = (body) => {
    navigator.clipboard?.writeText(body);
  };

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-sms-templates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#111111] flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-[#0D9488]" />
            SMS Templates
          </h1>
          <p className="text-sm text-[#64748B] mt-1">
            Create reusable SMS templates with variables like {'{{customer_name}}'}, {'{{booking_ref}}'} for one-click sending from Map Dispatch.
          </p>
        </div>
        <Button
          onClick={() => { setEditing(null); setShowModal(true); }}
          className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
          data-testid="new-template-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> New Template
        </Button>
      </div>

      {loading ? (
        <p className="text-[#64748B]">Loading...</p>
      ) : templates.length === 0 ? (
        <Card className="p-12 text-center">
          <MessageSquare className="w-12 h-12 text-[#CBD5E1] mx-auto mb-3" />
          <p className="text-[#64748B] mb-4">No SMS templates yet</p>
          <Button
            onClick={() => { setEditing(null); setShowModal(true); }}
            className="bg-[#0D9488] hover:bg-[#0F766E] text-white"
            data-testid="empty-new-template-btn"
          >
            <Plus className="w-4 h-4 mr-1" /> Create your first template
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {templates.map(t => (
            <Card key={t.id} className="p-4 hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-[#111111]">{t.name}</h3>
                  {t.description && <p className="text-xs text-[#64748B] mt-0.5">{t.description}</p>}
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => copyBody(t.body)}
                    className="p-1.5 text-[#64748B] hover:text-[#0D9488] hover:bg-[#F1F5F9] rounded"
                    title="Copy body"
                    data-testid={`copy-template-${t.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setEditing(t); setShowModal(true); }}
                    className="p-1.5 text-[#64748B] hover:text-[#0D9488] hover:bg-[#F1F5F9] rounded"
                    title="Edit"
                    data-testid={`edit-template-${t.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(t.id)}
                    className="p-1.5 text-[#64748B] hover:text-[#DC2626] hover:bg-red-50 rounded"
                    title="Delete"
                    data-testid={`delete-template-${t.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="bg-[#F8FAFC] rounded p-3 text-sm text-[#111111] whitespace-pre-wrap font-mono">
                {t.body}
              </div>
              <p className="text-[10px] text-[#94A3B8] mt-2">
                {t.body.length} chars {t.body.length > 160 ? `· ${Math.ceil(t.body.length / 160)} segments` : '· 1 segment'}
              </p>
            </Card>
          ))}
        </div>
      )}

      {showModal && (
        <TemplateModal
          template={editing}
          variables={variables.length ? variables : Object.keys(VARIABLE_LABELS)}
          onSave={handleSave}
          onClose={() => { setShowModal(false); setEditing(null); }}
        />
      )}
    </div>
  );
};

export default AdminSMSTemplates;
